import React, { useState, useEffect, useRef } from 'react';
import { Copy, Zap, ArrowRight, Check, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { apiCall } from '../utils/api';
import { useTheme } from '../context/ThemeContext';

export default function CampaignCloner({ onEditInBuilder }) {
  const { theme } = useTheme();
  const step2Ref = useRef(null);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [cloneType, setCloneType] = useState('language');
  const [cloneConfig, setCloneConfig] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [clonedCampaign, setClonedCampaign] = useState(null);
  const [error, setError] = useState(null);
  const [showChanges, setShowChanges] = useState(false);
  
  // Template state
  const [templates, setTemplates] = useState([]);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  
  // Bulk cloning state
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkTargets, setBulkTargets] = useState([]);
  const [bulkCloneResults, setBulkCloneResults] = useState(null);
  
  // History & Analytics state
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [historyFilter, setHistoryFilter] = useState('all');
  
  // Comparison view state
  const [showComparison, setShowComparison] = useState(false);
  const [comparisonData, setComparisonData] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    keywords: true,
    ads: true,
    targeting: true,
    budget: true
  });

  useEffect(() => {
    fetchCampaigns();
    fetchTemplates();
    if (showHistory) {
      fetchHistory();
    }
  }, [showHistory, historyFilter]);

  const fetchCampaigns = async () => {
    setLoadingCampaigns(true);
    setError(null);
    
    try {
      const response = await apiCall('/api/campaigns/list-for-cloning?limit=25&sortBy=spend');
      const data = await response.json();
      
      if (data.success) {
        const sortedCampaigns = (data.campaigns || [])
          .sort((a, b) => (b.cost || b.spend || 0) - (a.cost || a.spend || 0))
          .slice(0, 25);
        
        setCampaigns(sortedCampaigns);
      } else {
        setError(data.error || 'Failed to load campaigns');
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      setError('Failed to connect to server');
    } finally {
      setLoadingCampaigns(false);
    }
  };

  const handleClone = async () => {
    if (!selectedCampaign) {
      setError('Please select a campaign first');
      return;
    }
    
    // Bulk cloning mode
    if (bulkMode && bulkTargets.length > 0) {
      setLoading(true);
      setError(null);
      setBulkCloneResults(null);
      
      const results = [];
      const errors = [];
      
      console.log(`🔄 Starting bulk clone: ${bulkTargets.length} targets`);
      
      for (let i = 0; i < bulkTargets.length; i++) {
        try {
          console.log(`   Cloning ${i + 1}/${bulkTargets.length}...`);
          
          const response = await apiCall('/api/campaigns/clone-intelligent', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sourceCampaignId: selectedCampaign.id,
              cloneType,
              cloneConfig: bulkTargets[i]
            })
          });
          
          const data = await response.json();
          
          if (data.success) {
            results.push(data.cloned);
          } else {
            errors.push({ config: bulkTargets[i], error: data.error });
          }
        } catch (error) {
          errors.push({ config: bulkTargets[i], error: error.message });
        }
      }
      
      setLoading(false);
      setBulkCloneResults({ successes: results, errors });
      console.log(`✅ Bulk clone complete: ${results.length} succeeded, ${errors.length} failed`);
      
      return;
    }
    
    // Single cloning mode
    if (!cloneConfig || Object.keys(cloneConfig).length === 0) {
      setError('Please configure the clone settings');
      return;
    }
    
    setLoading(true);
    setError(null);
    setClonedCampaign(null);
    
    try {
      console.log('🔄 Starting clone process...');
      console.log('   Source:', selectedCampaign.id);
      console.log('   Type:', cloneType);
      console.log('   Config:', cloneConfig);
      
      const response = await apiCall('/api/campaigns/clone-intelligent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sourceCampaignId: selectedCampaign.id,
          cloneType,
          cloneConfig
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setClonedCampaign(data.cloned);
        console.log('✅ Campaign cloned successfully!');
      } else {
        setError(data.error || 'Failed to clone campaign');
      }
    } catch (error) {
      console.error('❌ Error cloning campaign:', error);
      setError('Failed to clone campaign: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Helper: Get label for target in bulk mode
  const getTargetLabel = (config, type) => {
    switch (type) {
      case 'language':
        return config.targetLanguage ? config.targetLanguage.toUpperCase() : 'Unknown';
      case 'location':
        return config.targetLocation || 'Unknown';
      case 'category':
        return config.newProduct || 'Unknown';
      case 'audience':
        return config.targetAudience || 'Unknown';
      case 'budget':
        return config.newBudget ? `€${config.newBudget}` : 'Unknown';
      default:
        return 'Unknown';
    }
  };

  // Template Management Functions
  const fetchTemplates = async () => {
    try {
      const response = await apiCall('/api/clone-templates');
      const data = await response.json();
      
      if (data.success) {
        setTemplates(data.templates);
        console.log(`✅ Loaded ${data.templates.length} templates`);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      setError('Please enter a template name');
      return;
    }
    
    try {
      const response = await apiCall('/api/clone-templates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: templateName,
          description: templateDescription,
          cloneType,
          config: cloneConfig
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setTemplates([...templates, data.template]);
        setShowSaveTemplate(false);
        setTemplateName('');
        setTemplateDescription('');
        console.log('✅ Template saved successfully');
      } else {
        setError('Failed to save template: ' + data.error);
      }
    } catch (error) {
      console.error('Error saving template:', error);
      setError('Failed to save template: ' + error.message);
    }
  };

  const handleUseTemplate = async (template) => {
    try {
      // Set the clone type and config from template
      setCloneType(template.cloneType);
      setCloneConfig(template.config);
      
      // Increment usage count
      await apiCall(`/api/clone-templates/${template.id}/use`, { method: 'PUT' });
      
      // Refresh templates to show updated usage count
      fetchTemplates();
      
      console.log(`✅ Using template: ${template.name}`);
    } catch (error) {
      console.error('Error using template:', error);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }
    
    try {
      const response = await apiCall(`/api/clone-templates/${templateId}`, { method: 'DELETE' });
      
      const data = await response.json();
      
      if (data.success) {
        setTemplates(templates.filter(t => t.id !== templateId));
        console.log('✅ Template deleted');
      } else {
        setError('Failed to delete template: ' + data.error);
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      setError('Failed to delete template: ' + error.message);
    }
  };

  // History Management Functions
  const fetchHistory = async () => {
    try {
      const params = new URLSearchParams();
      if (historyFilter !== 'all') {
        params.append('cloneType', historyFilter);
      }
      
      const response = await apiCall(`/api/clone-history?${params.toString()}`);
      const data = await response.json();
      
      if (data.success) {
        setHistory(data.history);
        setAnalytics(data.analytics);
        console.log(`✅ Loaded ${data.history.length} history entries`);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const handleDeleteHistoryEntry = async (historyId) => {
    if (!window.confirm('Are you sure you want to delete this history entry?')) {
      return;
    }
    
    try {
      const response = await apiCall(`/api/clone-history/${historyId}`, { method: 'DELETE' });
      
      const data = await response.json();
      
      if (data.success) {
        setHistory(history.filter(h => h.id !== historyId));
        console.log('✅ History entry deleted');
        
        // Refresh analytics
        fetchHistory();
      } else {
        setError('Failed to delete history entry: ' + data.error);
      }
    } catch (error) {
      console.error('Error deleting history entry:', error);
      setError('Failed to delete history entry: ' + error.message);
    }
  };

  const handleClearHistory = async () => {
    if (!window.confirm('Are you sure you want to clear ALL history? This cannot be undone.')) {
      return;
    }
    
    try {
      const response = await apiCall('/api/clone-history', { method: 'DELETE' });
      
      const data = await response.json();
      
      if (data.success) {
        setHistory([]);
        setAnalytics(null);
        console.log(`✅ Cleared ${data.cleared} history entries`);
      } else {
        setError('Failed to clear history: ' + data.error);
      }
    } catch (error) {
      console.error('Error clearing history:', error);
      setError('Failed to clear history: ' + error.message);
    }
  };

  // Comparison Functions
  const handleShowComparison = (sourceCampaign, clonedCampaign) => {
    setComparisonData({
      source: sourceCampaign,
      cloned: clonedCampaign
    });
    setShowComparison(true);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div style={{ padding: 40, maxWidth: 1400, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 40, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ 
            color: theme.colors.text.primary, 
            fontSize: 36, 
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 12
          }}>
            <Copy size={36} color="#8b5cf6" />
            Campaign Cloner
          </h1>
          <p style={{ color: theme.colors.text.tertiary, fontSize: 16 }}>
            Clone campaigns with AI-powered adaptations for different markets, audiences, and scenarios
          </p>
        </div>
        
        {/* View Toggle */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => setShowHistory(false)}
            style={{
              padding: '10px 20px',
              background: !showHistory ? 'rgba(139,92,246,0.3)' : 'rgba(255,255,255,0.05)',
              border: `1px solid ${!showHistory ? '#8b5cf6' : 'rgba(255,255,255,0.1)'}`,
              borderRadius: 8,
              color: 'white',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 600
            }}
          >
            Clone Campaign
          </button>
          <button
            onClick={() => setShowHistory(true)}
            style={{
              padding: '10px 20px',
              background: showHistory ? 'rgba(139,92,246,0.3)' : 'rgba(255,255,255,0.05)',
              border: `1px solid ${showHistory ? '#8b5cf6' : 'rgba(255,255,255,0.1)'}`,
              borderRadius: 8,
              color: 'white',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 600
            }}
          >
            📊 History & Analytics
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          background: 'rgba(239,68,68,0.1)',
          border: '2px solid rgba(239,68,68,0.3)',
          borderRadius: 12,
          padding: 16,
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 12
        }}>
          <AlertCircle size={20} style={{ color: '#ef4444' }} />
          <span style={{ color: '#ef4444', fontSize: 14 }}>{error}</span>
        </div>
      )}

      {/* History & Analytics View */}
      {showHistory ? (
        <div>
          {/* Analytics Cards */}
          {analytics && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 20, marginBottom: 30 }}>
              <div style={{
                background: 'linear-gradient(135deg, rgba(139,92,246,0.2) 0%, rgba(99,102,241,0.1) 100%)',
                border: '1px solid rgba(139,92,246,0.3)',
                borderRadius: 16,
                padding: 24
              }}>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, marginBottom: 8 }}>Total Clones</div>
                <div style={{ color: 'white', fontSize: 32, fontWeight: 700 }}>{analytics.total}</div>
              </div>
              
              {Object.entries(analytics.byType).map(([type, count]) => (
                <div key={type} style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 16,
                  padding: 24
                }}>
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, marginBottom: 8, textTransform: 'capitalize' }}>
                    {type} Clones
                  </div>
                  <div style={{ color: 'white', fontSize: 32, fontWeight: 700 }}>{count}</div>
                </div>
              ))}
            </div>
          )}

          {/* Filter Tabs */}
          <div style={{ 
            display: 'flex', 
            gap: 12, 
            marginBottom: 20,
            flexWrap: 'wrap'
          }}>
            {['all', 'language', 'location', 'category', 'audience', 'budget'].map(filter => (
              <button
                key={filter}
                onClick={() => setHistoryFilter(filter)}
                style={{
                  padding: '8px 16px',
                  background: historyFilter === filter ? 'rgba(139,92,246,0.3)' : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${historyFilter === filter ? '#8b5cf6' : 'rgba(255,255,255,0.1)'}`,
                  borderRadius: 8,
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                  textTransform: 'capitalize'
                }}
              >
                {filter === 'all' ? 'All Types' : filter}
              </button>
            ))}
            
            {history.length > 0 && (
              <button
                onClick={handleClearHistory}
                style={{
                  marginLeft: 'auto',
                  padding: '8px 16px',
                  background: 'rgba(239,68,68,0.1)',
                  border: '1px solid rgba(239,68,68,0.3)',
                  borderRadius: 8,
                  color: '#ef4444',
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600
                }}
              >
                Clear All History
              </button>
            )}
          </div>

          {/* History List */}
          {history.length === 0 ? (
            <div style={{
              background: 'rgba(255,255,255,0.05)',
              borderRadius: 20,
              padding: 60,
              textAlign: 'center'
            }}>
              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 16 }}>
                No clone history yet. Clone a campaign to see it here.
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 16 }}>
              {history.map(entry => (
                <div key={entry.id} style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 16,
                  padding: 20
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 12,
                        marginBottom: 8
                      }}>
                        <div style={{
                          background: 'rgba(139,92,246,0.2)',
                          border: '1px solid rgba(139,92,246,0.4)',
                          borderRadius: 6,
                          padding: '4px 10px',
                          fontSize: 12,
                          color: '#8b5cf6',
                          fontWeight: 600,
                          textTransform: 'capitalize'
                        }}>
                          {entry.cloneType}
                        </div>
                        <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>
                          {new Date(entry.timestamp).toLocaleString()}
                        </div>
                      </div>
                      
                      <div style={{ color: 'white', fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
                        {entry.sourceCampaignName} → {entry.clonedCampaignName}
                      </div>
                      
                      <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14 }}>
                        {entry.changesCount} AI-powered changes applied
                      </div>
                    </div>
                    
                    <button
                      onClick={() => handleDeleteHistoryEntry(entry.id)}
                      style={{
                        padding: '6px 12px',
                        background: 'rgba(239,68,68,0.1)',
                        border: '1px solid rgba(239,68,68,0.3)',
                        borderRadius: 6,
                        color: '#ef4444',
                        cursor: 'pointer',
                        fontSize: 13,
                        fontWeight: 600
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Step 1: Campaign Selection */}
      <div style={{ 
        background: 'rgba(255,255,255,0.05)', 
        borderRadius: 20, 
        padding: 30,
        marginBottom: 24
      }}>
        <h3 style={{ color: 'white', fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
          1. Select Campaign to Clone
        </h3>
        
        {loadingCampaigns ? (
          <div style={{ color: 'rgba(255,255,255,0.6)', padding: 20, textAlign: 'center' }}>
            Loading campaigns...
          </div>
        ) : campaigns.length === 0 ? (
          <div style={{ color: 'rgba(255,255,255,0.6)', padding: 20, textAlign: 'center' }}>
            No campaigns available. Create a campaign first using the AI Campaign Generator.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            {campaigns.map(campaign => (
              <div
                key={campaign.id}
                onClick={() => {
                  setSelectedCampaign(campaign);
                  setClonedCampaign(null);
                  setError(null);
                  // Auto-scroll to step 2
                  setTimeout(() => {
                    step2Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 100);
                }}
                style={{
                  padding: 20,
                  background: selectedCampaign?.id === campaign.id 
                    ? 'rgba(139,92,246,0.2)' 
                    : 'rgba(0,0,0,0.3)',
                  border: `2px solid ${selectedCampaign?.id === campaign.id ? '#8b5cf6' : 'rgba(255,255,255,0.1)'}`,
                  borderRadius: 12,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: 'white', fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
                      {campaign.name}
                    </div>
                    <div style={{ 
                      color: 'rgba(255,255,255,0.6)', 
                      fontSize: 13,
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 16,
                      alignItems: 'center'
                    }}>
                      <span style={{ 
                        background: campaign.status === 'ENABLED' ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.1)',
                        color: campaign.status === 'ENABLED' ? '#10b981' : 'rgba(255,255,255,0.6)',
                        padding: '2px 8px',
                        borderRadius: 4,
                        fontSize: 11,
                        fontWeight: 600
                      }}>
                        {campaign.status === 'ENABLED' ? '● Active' : campaign.status || 'Draft'}
                      </span>
                      {campaign.spend > 0 && (
                        <span style={{ fontWeight: 600, color: '#10b981' }}>
                          💰 €{campaign.spend.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} spent
                        </span>
                      )}
                      {campaign.clicks > 0 && <span>🖱️ {campaign.clicks.toLocaleString()} clicks</span>}
                      {campaign.conversions > 0 && <span>🎯 {Math.round(campaign.conversions)} conversions</span>}
                      {campaign.budget && <span>Budget: €{campaign.budget}/day</span>}
                    </div>
                  </div>
                  {selectedCampaign?.id === campaign.id && (
                    <Check size={24} style={{ color: '#8b5cf6', marginLeft: 16 }} />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedCampaign && (
        <>
          {/* Step 2: Clone Type Selection */}
          <div 
            ref={step2Ref}
            style={{ 
              background: 'rgba(255,255,255,0.05)', 
              borderRadius: 20, 
              padding: 30,
              marginBottom: 24
            }}
          >
            <h3 style={{ color: 'white', fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
              2. Select Clone Type
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
              {[
                { id: 'language', label: 'Language/Culture', priority: 5, icon: '🌍' },
                { id: 'location', label: 'Location', priority: 5, icon: '📍' },
                { id: 'category', label: 'Category/Product', priority: 5, icon: '🏷️' },
                { id: 'audience', label: 'Audience', priority: 3, icon: '👥' },
                { id: 'budget', label: 'Budget Scale', priority: 2, icon: '💰' }
              ].map(type => (
                <button
                  key={type.id}
                  onClick={() => {
                    setCloneType(type.id);
                    setCloneConfig({});
                    setClonedCampaign(null);
                    setError(null);
                  }}
                  style={{
                    padding: 16,
                    background: cloneType === type.id ? 'rgba(139,92,246,0.3)' : 'rgba(0,0,0,0.3)',
                    border: `2px solid ${cloneType === type.id ? '#8b5cf6' : 'rgba(255,255,255,0.1)'}`,
                    borderRadius: 12,
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 600,
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ fontSize: 24, marginBottom: 8 }}>{type.icon}</div>
                  {type.label}
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>
                    Priority {type.priority}
                  </div>
                </button>
              ))}
            </div>
            
            {/* Bulk Mode Toggle */}
            <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
              <label style={{ 
                color: 'white', 
                fontSize: 14, 
                display: 'flex', 
                alignItems: 'center', 
                gap: 8,
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={bulkMode}
                  onChange={(e) => {
                    setBulkMode(e.target.checked);
                    if (!e.target.checked) {
                      setBulkTargets([]);
                      setBulkCloneResults(null);
                    }
                  }}
                  style={{ 
                    width: 18, 
                    height: 18,
                    cursor: 'pointer'
                  }}
                />
                <span>Enable Bulk Cloning (clone to multiple targets at once)</span>
              </label>
            </div>

            {/* Bulk Targets Configuration */}
            {bulkMode && (
              <div style={{ marginTop: 16 }}>
                <h4 style={{ color: 'white', fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
                  Bulk Targets
                </h4>
                
                {/* Current Targets */}
                {bulkTargets.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                    {bulkTargets.map((target, i) => (
                      <div key={i} style={{
                        background: 'rgba(139,92,246,0.2)',
                        border: '1px solid rgba(139,92,246,0.4)',
                        borderRadius: 8,
                        padding: '8px 12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                      }}>
                        <span style={{ color: 'white', fontSize: 13 }}>
                          {getTargetLabel(target, cloneType)}
                        </span>
                        <button
                          onClick={() => {
                            setBulkTargets(bulkTargets.filter((_, idx) => idx !== i));
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'rgba(255,255,255,0.6)',
                            cursor: 'pointer',
                            padding: 0,
                            fontSize: 18,
                            lineHeight: 1
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Add Target Button */}
                <button
                  onClick={() => {
                    if (Object.keys(cloneConfig).length > 0) {
                      setBulkTargets([...bulkTargets, { ...cloneConfig }]);
                      setCloneConfig({});
                    }
                  }}
                  disabled={Object.keys(cloneConfig).length === 0}
                  style={{
                    padding: '10px 16px',
                    background: Object.keys(cloneConfig).length === 0
                      ? 'rgba(139,92,246,0.1)'
                      : 'rgba(139,92,246,0.2)',
                    border: '1px solid rgba(139,92,246,0.4)',
                    borderRadius: 8,
                    color: Object.keys(cloneConfig).length === 0
                      ? 'rgba(139,92,246,0.4)'
                      : '#8b5cf6',
                    cursor: Object.keys(cloneConfig).length === 0 ? 'not-allowed' : 'pointer',
                    fontSize: 14,
                    fontWeight: 600
                  }}
                >
                  + Add Target
                </button>
                
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, marginTop: 8 }}>
                  {bulkTargets.length} target{bulkTargets.length !== 1 ? 's' : ''} configured
                  {bulkTargets.length > 0 && ' • Configure another target below and click "Add Target"'}
                </div>
              </div>
            )}
          </div>

          {/* Template Management */}
          {templates.length > 0 && (
            <div style={{
              background: 'rgba(255,255,255,0.05)',
              borderRadius: 20,
              padding: 30,
              marginBottom: 24
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 16
              }}>
                <h3 style={{ color: 'white', fontSize: 18, fontWeight: 600, margin: 0 }}>
                  📚 Saved Templates
                </h3>
                <button
                  onClick={() => setShowSaveTemplate(true)}
                  disabled={Object.keys(cloneConfig).length === 0}
                  style={{
                    padding: '8px 16px',
                    background: Object.keys(cloneConfig).length === 0
                      ? 'rgba(139,92,246,0.1)'
                      : 'rgba(139,92,246,0.2)',
                    border: '1px solid rgba(139,92,246,0.4)',
                    borderRadius: 8,
                    color: Object.keys(cloneConfig).length === 0
                      ? 'rgba(139,92,246,0.4)'
                      : '#8b5cf6',
                    cursor: Object.keys(cloneConfig).length === 0 ? 'not-allowed' : 'pointer',
                    fontSize: 14,
                    fontWeight: 600
                  }}
                >
                  💾 Save Current Config as Template
                </button>
              </div>

              {/* Template List */}
              <div style={{ display: 'grid', gap: 12 }}>
                {templates.map(template => (
                  <div key={template.id} style={{
                    background: 'rgba(0,0,0,0.3)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 12,
                    padding: 16,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: 'white', fontSize: 15, fontWeight: 600, marginBottom: 4 }}>
                        {template.name}
                      </div>
                      <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
                        {template.cloneType} • {template.description || 'No description'} • Used {template.usageCount} times
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => handleUseTemplate(template)}
                        style={{
                          padding: '6px 12px',
                          background: 'rgba(16,185,129,0.2)',
                          border: '1px solid rgba(16,185,129,0.4)',
                          borderRadius: 6,
                          color: '#10b981',
                          cursor: 'pointer',
                          fontSize: 13,
                          fontWeight: 600
                        }}
                      >
                        Use Template
                      </button>
                      <button
                        onClick={() => handleDeleteTemplate(template.id)}
                        style={{
                          padding: '6px 12px',
                          background: 'rgba(239,68,68,0.1)',
                          border: '1px solid rgba(239,68,68,0.3)',
                          borderRadius: 6,
                          color: '#ef4444',
                          cursor: 'pointer',
                          fontSize: 13,
                          fontWeight: 600
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Configuration Panel */}
          <CloneConfigPanel 
            cloneType={cloneType}
            config={cloneConfig}
            onChange={setCloneConfig}
            sourceCampaign={selectedCampaign}
          />

          {/* Step 4: Clone Button */}
          <button
            onClick={handleClone}
            disabled={loading || (bulkMode ? bulkTargets.length === 0 : Object.keys(cloneConfig).length === 0)}
            style={{
              width: '100%',
              padding: 20,
              background: loading || (bulkMode ? bulkTargets.length === 0 : Object.keys(cloneConfig).length === 0)
                ? 'rgba(139,92,246,0.3)' 
                : 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
              border: 'none',
              borderRadius: 12,
              color: 'white',
              fontSize: 18,
              fontWeight: 700,
              cursor: loading || (bulkMode ? bulkTargets.length === 0 : Object.keys(cloneConfig).length === 0) ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
              marginBottom: 24,
              transition: 'all 0.2s'
            }}
          >
            {loading ? (
              <>
                <Zap size={24} className="animate-pulse" />
                {bulkMode ? `Cloning ${bulkTargets.length} campaigns...` : 'Cloning with AI...'}
              </>
            ) : (
              <>
                <Zap size={24} />
                {bulkMode ? `Clone to ${bulkTargets.length} Targets` : 'Clone Campaign'}
                <ArrowRight size={24} />
              </>
            )}
          </button>

          {/* Cloned Result */}
          {clonedCampaign && (
            <div style={{
              background: 'rgba(16,185,129,0.1)',
              border: '2px solid rgba(16,185,129,0.3)',
              borderRadius: 20,
              padding: 30
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h3 style={{ color: '#10b981', fontSize: 20, fontWeight: 600, margin: 0 }}>
                  ✅ Campaign Cloned Successfully
                </h3>
                <button
                  onClick={() => setShowChanges(!showChanges)}
                  style={{
                    background: 'rgba(16,185,129,0.2)',
                    border: '1px solid rgba(16,185,129,0.4)',
                    borderRadius: 8,
                    padding: '8px 16px',
                    color: '#10b981',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 14
                  }}
                >
                  {showChanges ? 'Hide' : 'Show'} Details
                  {showChanges ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
              </div>

              {/* Summary */}
              <div style={{ 
                background: 'rgba(0,0,0,0.3)', 
                padding: 20, 
                borderRadius: 12,
                marginBottom: 16
              }}>
                <div style={{ color: 'white', fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
                  {clonedCampaign.name}
                </div>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: 12,
                  color: 'rgba(255,255,255,0.8)',
                  fontSize: 14
                }}>
                  <div>💰 Budget: €{clonedCampaign.budget}/day</div>
                  {clonedCampaign.language && <div>🌍 Language: {clonedCampaign.language.toUpperCase()}</div>}
                  {clonedCampaign.locations && <div>📍 {clonedCampaign.locations.join(', ')}</div>}
                  <div>🔑 {clonedCampaign.keywords?.length || 0} keywords</div>
                  <div>📝 {clonedCampaign.ads?.length || 0} ad variations</div>
                </div>
              </div>

              {/* Detailed Changes */}
              {showChanges && (
                <div style={{ 
                  background: 'rgba(0,0,0,0.3)', 
                  padding: 20, 
                  borderRadius: 12 
                }}>
                  <h4 style={{ color: 'white', fontSize: 16, marginBottom: 16 }}>Keywords:</h4>
                  <div style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 8,
                    marginBottom: 20 
                  }}>
                    {clonedCampaign.keywords?.slice(0, 10).map((kw, i) => (
                      <span key={i} style={{
                        background: 'rgba(139,92,246,0.2)',
                        border: '1px solid rgba(139,92,246,0.4)',
                        borderRadius: 6,
                        padding: '6px 12px',
                        color: '#a78bfa',
                        fontSize: 13
                      }}>
                        {kw.text}
                      </span>
                    ))}
                    {clonedCampaign.keywords?.length > 10 && (
                      <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, padding: '6px 12px' }}>
                        +{clonedCampaign.keywords.length - 10} more
                      </span>
                    )}
                  </div>

                  {clonedCampaign.ads && clonedCampaign.ads.length > 0 && (
                    <>
                      <h4 style={{ color: 'white', fontSize: 16, marginBottom: 12 }}>Sample Ad:</h4>
                      <div style={{
                        background: 'rgba(139,92,246,0.1)',
                        border: '1px solid rgba(139,92,246,0.3)',
                        borderRadius: 8,
                        padding: 16
                      }}>
                        <div style={{ marginBottom: 12 }}>
                          {clonedCampaign.ads[0].headlines.map((h, i) => (
                            <div key={i} style={{ 
                              color: '#8b5cf6', 
                              fontSize: 14, 
                              fontWeight: 600,
                              marginBottom: 4 
                            }}>
                              {h}
                            </div>
                          ))}
                        </div>
                        {clonedCampaign.ads[0].descriptions.map((d, i) => (
                          <div key={i} style={{ 
                            color: 'rgba(255,255,255,0.8)', 
                            fontSize: 13,
                            marginBottom: 4 
                          }}>
                            {d}
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {clonedCampaign.cloneMetadata && (
                    <div style={{ 
                      marginTop: 16, 
                      padding: 12, 
                      background: 'rgba(59,130,246,0.1)',
                      border: '1px solid rgba(59,130,246,0.3)',
                      borderRadius: 8,
                      fontSize: 13,
                      color: 'rgba(255,255,255,0.8)'
                    }}>
                      <strong>Transformation Notes:</strong>
                      <pre style={{ 
                        marginTop: 8, 
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'inherit'
                      }}>
                        {JSON.stringify(clonedCampaign.cloneMetadata, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ 
                display: 'flex', 
                gap: 12, 
                marginTop: 20 
              }}>
                <button
                  onClick={() => handleShowComparison(selectedCampaign, clonedCampaign)}
                  style={{
                    flex: 1,
                    padding: 14,
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    border: 'none',
                    borderRadius: 10,
                    color: 'white',
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  🔍 View Comparison
                </button>
                <button
                  onClick={() => {
                    if (onEditInBuilder) {
                      onEditInBuilder(clonedCampaign);
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: 14,
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    border: 'none',
                    borderRadius: 10,
                    color: 'white',
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Edit in Campaign Builder
                </button>
                <button
                  onClick={() => {
                    setClonedCampaign(null);
                    setSelectedCampaign(null);
                    setCloneConfig({});
                  }}
                  style={{
                    padding: 14,
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: 10,
                    color: 'white',
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Clone Another
                </button>
              </div>
            </div>
          )}

          {/* Bulk Clone Results */}
          {bulkCloneResults && (
            <div style={{
              background: 'rgba(16,185,129,0.1)',
              border: '2px solid rgba(16,185,129,0.3)',
              borderRadius: 20,
              padding: 30
            }}>
              <h3 style={{ color: '#10b981', fontSize: 20, fontWeight: 600, marginBottom: 20 }}>
                ✅ Bulk Clone Complete
              </h3>
              
              <div style={{ marginBottom: 20 }}>
                <div style={{ color: 'white', fontSize: 16, marginBottom: 12 }}>
                  Successfully cloned: <strong>{bulkCloneResults.successes.length}</strong> campaign{bulkCloneResults.successes.length !== 1 ? 's' : ''}
                </div>
                {bulkCloneResults.errors.length > 0 && (
                  <div style={{ color: '#ef4444', fontSize: 14 }}>
                    Failed: <strong>{bulkCloneResults.errors.length}</strong> campaign{bulkCloneResults.errors.length !== 1 ? 's' : ''}
                  </div>
                )}
              </div>
              
              {/* List of cloned campaigns */}
              {bulkCloneResults.successes.length > 0 && (
                <>
                  <h4 style={{ color: 'white', fontSize: 16, marginBottom: 12 }}>
                    Successfully Cloned Campaigns:
                  </h4>
                  <div style={{ display: 'grid', gap: 12, marginBottom: 20 }}>
                    {bulkCloneResults.successes.map((campaign, i) => (
                      <div key={i} style={{
                        background: 'rgba(0,0,0,0.3)',
                        padding: 16,
                        borderRadius: 12,
                        border: '1px solid rgba(16,185,129,0.2)'
                      }}>
                        <div style={{ color: 'white', fontWeight: 600, marginBottom: 6, fontSize: 15 }}>
                          {campaign.name}
                        </div>
                        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
                          {campaign.keywords?.length || 0} keywords • {campaign.ads?.length || 0} ads • Budget: €{campaign.budget}/day
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* List of failed clones */}
              {bulkCloneResults.errors.length > 0 && (
                <>
                  <h4 style={{ color: '#ef4444', fontSize: 16, marginBottom: 12 }}>
                    Failed Clones:
                  </h4>
                  <div style={{ display: 'grid', gap: 12, marginBottom: 20 }}>
                    {bulkCloneResults.errors.map((error, i) => (
                      <div key={i} style={{
                        background: 'rgba(239,68,68,0.1)',
                        padding: 16,
                        borderRadius: 12,
                        border: '1px solid rgba(239,68,68,0.3)'
                      }}>
                        <div style={{ color: '#ef4444', fontWeight: 600, marginBottom: 4, fontSize: 14 }}>
                          Target: {getTargetLabel(error.config, cloneType)}
                        </div>
                        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
                          Error: {error.error}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  onClick={() => {
                    setBulkCloneResults(null);
                    setBulkTargets([]);
                    setBulkMode(false);
                    setSelectedCampaign(null);
                  }}
                  style={{
                    flex: 1,
                    padding: 14,
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: 10,
                    color: 'white',
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Clone Another Campaign
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Save Template Modal */}
      {showSaveTemplate && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            borderRadius: 20,
            padding: 30,
            maxWidth: 500,
            width: '90%'
          }}>
            <h3 style={{ color: 'white', fontSize: 20, fontWeight: 600, marginBottom: 20 }}>
              💾 Save Clone Template
            </h3>
            
            <input
              type="text"
              placeholder="Template name..."
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              style={{
                width: '100%',
                padding: 12,
                borderRadius: 8,
                background: 'rgba(0,0,0,0.3)',
                border: '1px solid rgba(255,255,255,0.2)',
                color: 'white',
                fontSize: 14,
                marginBottom: 12
              }}
            />
            
            <textarea
              placeholder="Description (optional)..."
              value={templateDescription}
              onChange={(e) => setTemplateDescription(e.target.value)}
              style={{
                width: '100%',
                padding: 12,
                borderRadius: 8,
                background: 'rgba(0,0,0,0.3)',
                border: '1px solid rgba(255,255,255,0.2)',
                color: 'white',
                fontSize: 14,
                marginBottom: 12,
                minHeight: 80,
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
            />

            <div style={{
              background: 'rgba(139,92,246,0.1)',
              border: '1px solid rgba(139,92,246,0.3)',
              borderRadius: 8,
              padding: 12,
              marginBottom: 20
            }}>
              <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13, marginBottom: 6 }}>
                <strong>Clone Type:</strong> {cloneType}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>
                Config: {JSON.stringify(cloneConfig)}
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={handleSaveTemplate}
                disabled={!templateName.trim()}
                style={{
                  flex: 1,
                  padding: 14,
                  background: !templateName.trim()
                    ? 'rgba(139,92,246,0.3)'
                    : 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                  border: 'none',
                  borderRadius: 10,
                  color: 'white',
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: !templateName.trim() ? 'not-allowed' : 'pointer'
                }}
              >
                Save Template
              </button>
              <button
                onClick={() => {
                  setShowSaveTemplate(false);
                  setTemplateName('');
                  setTemplateDescription('');
                }}
                style={{
                  padding: 14,
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: 10,
                  color: 'white',
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      )}

      {/* Comparison Modal */}
      {showComparison && comparisonData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: 20,
          overflowY: 'auto'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            borderRadius: 20,
            padding: 40,
            maxWidth: 1200,
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 30 }}>
              <h2 style={{ color: 'white', fontSize: 24, fontWeight: 700, margin: 0 }}>
                🔍 Campaign Comparison
              </h2>
              <button
                onClick={() => setShowComparison(false)}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: 'none',
                  borderRadius: 8,
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: 24,
                  width: 40,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            {/* Side by Side Headers */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
              <div style={{
                background: 'rgba(59,130,246,0.1)',
                border: '1px solid rgba(59,130,246,0.3)',
                borderRadius: 12,
                padding: 16,
                textAlign: 'center'
              }}>
                <div style={{ color: '#3b82f6', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
                  Original Campaign
                </div>
                <div style={{ color: 'white', fontSize: 16, fontWeight: 700 }}>
                  {comparisonData.source.name}
                </div>
              </div>
              <div style={{
                background: 'rgba(16,185,129,0.1)',
                border: '1px solid rgba(16,185,129,0.3)',
                borderRadius: 12,
                padding: 16,
                textAlign: 'center'
              }}>
                <div style={{ color: '#10b981', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
                  Cloned Campaign
                </div>
                <div style={{ color: 'white', fontSize: 16, fontWeight: 700 }}>
                  {comparisonData.cloned.name}
                </div>
              </div>
            </div>

            {/* Budget & Basic Info */}
            <ComparisonSection
              title="Budget & Settings"
              expanded={expandedSections.budget}
              onToggle={() => toggleSection('budget')}
            >
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                <ComparisonItem label="Daily Budget" before={`€${comparisonData.source.budget}`} after={`€${comparisonData.cloned.budget}`} />
                <ComparisonItem label="Currency" before={comparisonData.source.currency} after={comparisonData.cloned.currency} />
                <ComparisonItem 
                  label="Language" 
                  before={comparisonData.source.language?.toUpperCase() || comparisonData.source.languages?.join(', ').toUpperCase() || 'N/A'} 
                  after={comparisonData.cloned.language?.toUpperCase() || comparisonData.cloned.languages?.join(', ').toUpperCase() || 'N/A'} 
                />
                <ComparisonItem 
                  label="Locations" 
                  before={comparisonData.source.locations?.join(', ') || 'N/A'} 
                  after={comparisonData.cloned.locations?.join(', ') || 'N/A'} 
                />
              </div>
            </ComparisonSection>

            {/* Keywords */}
            <ComparisonSection
              title="Keywords"
              expanded={expandedSections.keywords}
              onToggle={() => toggleSection('keywords')}
            >
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                <div>
                  <div style={{ color: '#3b82f6', fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
                    Original ({comparisonData.source.keywords?.length || 0} keywords)
                  </div>
                  <div style={{ display: 'grid', gap: 8 }}>
                    {(comparisonData.source.keywords || []).slice(0, 10).map((kw, i) => (
                      <div key={i} style={{
                        background: 'rgba(59,130,246,0.1)',
                        border: '1px solid rgba(59,130,246,0.2)',
                        borderRadius: 8,
                        padding: '8px 12px',
                        color: 'white',
                        fontSize: 13
                      }}>
                        {kw.text || kw}
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#10b981', fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
                    Cloned ({comparisonData.cloned.keywords?.length || 0} keywords)
                  </div>
                  <div style={{ display: 'grid', gap: 8 }}>
                    {(comparisonData.cloned.keywords || []).slice(0, 10).map((kw, i) => (
                      <div key={i} style={{
                        background: 'rgba(16,185,129,0.1)',
                        border: '1px solid rgba(16,185,129,0.2)',
                        borderRadius: 8,
                        padding: '8px 12px',
                        color: 'white',
                        fontSize: 13
                      }}>
                        {kw.text || kw}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </ComparisonSection>

            {/* Ads */}
            <ComparisonSection
              title="Ads"
              expanded={expandedSections.ads}
              onToggle={() => toggleSection('ads')}
            >
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                <div>
                  <div style={{ color: '#3b82f6', fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
                    Original ({comparisonData.source.ads?.length || 0} ads)
                  </div>
                  <div style={{ display: 'grid', gap: 12 }}>
                    {(comparisonData.source.ads || []).slice(0, 3).map((ad, i) => (
                      <div key={i} style={{
                        background: 'rgba(59,130,246,0.1)',
                        border: '1px solid rgba(59,130,246,0.2)',
                        borderRadius: 8,
                        padding: 12
                      }}>
                        <div style={{ color: 'white', fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
                          {ad.headlines?.[0] || ad.headline1 || 'No headline'}
                        </div>
                        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>
                          {ad.descriptions?.[0] || ad.description1 || 'No description'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#10b981', fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
                    Cloned ({comparisonData.cloned.ads?.length || 0} ads)
                  </div>
                  <div style={{ display: 'grid', gap: 12 }}>
                    {(comparisonData.cloned.ads || []).slice(0, 3).map((ad, i) => (
                      <div key={i} style={{
                        background: 'rgba(16,185,129,0.1)',
                        border: '1px solid rgba(16,185,129,0.2)',
                        borderRadius: 8,
                        padding: 12
                      }}>
                        <div style={{ color: 'white', fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
                          {ad.headlines?.[0] || ad.headline1 || 'No headline'}
                        </div>
                        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>
                          {ad.descriptions?.[0] || ad.description1 || 'No description'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </ComparisonSection>

            {/* Close Button */}
            <div style={{ marginTop: 30, textAlign: 'center' }}>
              <button
                onClick={() => setShowComparison(false)}
                style={{
                  padding: '12px 32px',
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: 10,
                  color: 'white',
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Close Comparison
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper Components for Comparison View
const ComparisonSection = ({ title, expanded, onToggle, children }) => (
  <div style={{
    background: 'rgba(255,255,255,0.05)',
    borderRadius: 12,
    padding: 20,
    marginBottom: 16
  }}>
    <div
      onClick={onToggle}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        cursor: 'pointer',
        marginBottom: expanded ? 16 : 0
      }}
    >
      <h3 style={{ color: 'white', fontSize: 18, fontWeight: 600, margin: 0 }}>
        {title}
      </h3>
      <ChevronDown
        size={20}
        style={{
          color: 'rgba(255,255,255,0.6)',
          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s'
        }}
      />
    </div>
    {expanded && children}
  </div>
);

const ComparisonItem = ({ label, before, after }) => {
  const isChanged = before !== after;
  return (
    <div style={{
      background: isChanged ? 'rgba(251,191,36,0.1)' : 'rgba(255,255,255,0.03)',
      border: `1px solid ${isChanged ? 'rgba(251,191,36,0.3)' : 'rgba(255,255,255,0.1)'}`,
      borderRadius: 8,
      padding: 12
    }}>
      <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ color: isChanged ? '#3b82f6' : 'white', fontSize: 14 }}>{before}</span>
        {isChanged && (
          <>
            <ArrowRight size={16} style={{ color: '#fbbf24' }} />
            <span style={{ color: '#10b981', fontSize: 14, fontWeight: 600 }}>{after}</span>
          </>
        )}
      </div>
    </div>
  );
};

// Configuration panel component for different clone types
const CloneConfigPanel = ({ cloneType, config, onChange, sourceCampaign }) => {
  const panels = {
    language: (
      <div>
        <label style={{ color: 'white', fontSize: 14, display: 'block', marginBottom: 8 }}>
          Target Language
        </label>
        <select
          value={config.targetLanguage || ''}
          onChange={(e) => onChange({ ...config, targetLanguage: e.target.value })}
          style={{
            width: '100%',
            padding: 12,
            borderRadius: 8,
            background: 'rgba(0,0,0,0.3)',
            border: '1px solid rgba(255,255,255,0.2)',
            color: 'white',
            fontSize: 14
          }}
        >
          <option value="">Select language...</option>
          <option value="nl">🇳🇱 Nederlands (Dutch)</option>
          <option value="en">🇬🇧 English</option>
          <option value="de">🇩🇪 Deutsch (German)</option>
          <option value="fr">🇫🇷 Français (French)</option>
          <option value="es">🇪🇸 Español (Spanish)</option>
          <option value="it">🇮🇹 Italiano (Italian)</option>
          <option value="pt">🇵🇹 Português (Portuguese)</option>
        </select>
        <div style={{ 
          color: 'rgba(255,255,255,0.6)', 
          fontSize: 12, 
          marginTop: 8 
        }}>
          Campaign will be translated and culturally adapted for the target market
        </div>
      </div>
    ),
    location: (
      <div>
        <label style={{ color: 'white', fontSize: 14, display: 'block', marginBottom: 8 }}>
          Target Location
        </label>
        <input
          type="text"
          value={config.targetLocation || ''}
          onChange={(e) => onChange({ ...config, targetLocation: e.target.value })}
          placeholder="e.g., Barcelona, Amsterdam, Paris..."
          style={{
            width: '100%',
            padding: 12,
            borderRadius: 8,
            background: 'rgba(0,0,0,0.3)',
            border: '1px solid rgba(255,255,255,0.2)',
            color: 'white',
            fontSize: 14
          }}
        />
        <div style={{ 
          color: 'rgba(255,255,255,0.6)', 
          fontSize: 12, 
          marginTop: 8 
        }}>
          Current location: {sourceCampaign.locations?.join(', ') || 'Not specified'}
        </div>
      </div>
    ),
    audience: (
      <div>
        <label style={{ color: 'white', fontSize: 14, display: 'block', marginBottom: 8 }}>
          New Target Audience
        </label>
        <input
          type="text"
          value={config.targetAudience || ''}
          onChange={(e) => onChange({ ...config, targetAudience: e.target.value })}
          placeholder="e.g., locals, business travelers, families..."
          style={{
            width: '100%',
            padding: 12,
            borderRadius: 8,
            background: 'rgba(0,0,0,0.3)',
            border: '1px solid rgba(255,255,255,0.2)',
            color: 'white',
            fontSize: 14
          }}
        />
        <div style={{ 
          color: 'rgba(255,255,255,0.6)', 
          fontSize: 12, 
          marginTop: 8 
        }}>
          Current audience: {sourceCampaign.targetAudience || 'Not specified'}
        </div>
      </div>
    ),
    budget: (
      <div>
        <label style={{ color: 'white', fontSize: 14, display: 'block', marginBottom: 8 }}>
          New Budget (€/day)
        </label>
        <input
          type="number"
          value={config.newBudget || ''}
          onChange={(e) => onChange({ ...config, newBudget: parseFloat(e.target.value) })}
          placeholder={`Current: €${sourceCampaign.budget}/day`}
          min="1"
          step="1"
          style={{
            width: '100%',
            padding: 12,
            borderRadius: 8,
            background: 'rgba(0,0,0,0.3)',
            border: '1px solid rgba(255,255,255,0.2)',
            color: 'white',
            fontSize: 14
          }}
        />
        <div style={{ 
          color: 'rgba(255,255,255,0.6)', 
          fontSize: 12, 
          marginTop: 8 
        }}>
          Current budget: €{sourceCampaign.budget}/day
        </div>
      </div>
    ),
    category: (
      <div>
        <label style={{ color: 'white', fontSize: 14, display: 'block', marginBottom: 8 }}>
          New Product/Service
        </label>
        <input
          type="text"
          value={config.newProduct || ''}
          onChange={(e) => onChange({ 
            ...config, 
            newProduct: e.target.value,
            newCategory: e.target.value 
          })}
          placeholder="e.g., walking tours, yoga classes, cooking workshops..."
          style={{
            width: '100%',
            padding: 12,
            borderRadius: 8,
            background: 'rgba(0,0,0,0.3)',
            border: '1px solid rgba(255,255,255,0.2)',
            color: 'white',
            fontSize: 14
          }}
        />
        <div style={{ 
          color: 'rgba(255,255,255,0.6)', 
          fontSize: 12, 
          marginTop: 8 
        }}>
          Current category: {sourceCampaign.category || sourceCampaign.businessType || 'Not specified'}
        </div>
      </div>
    )
  };

  return (
    <div style={{ 
      background: 'rgba(255,255,255,0.05)', 
      borderRadius: 20, 
      padding: 30,
      marginBottom: 24
    }}>
      <h3 style={{ color: 'white', fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
        3. Configure Clone Settings
      </h3>
      {panels[cloneType]}
    </div>
  );
};

